import { FC, lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes as RoutesBase } from 'react-router-dom';
import { isInStandaloneMode } from '@/ui-kit/utils/isInStandaloneMode';
import { UseDoLoginMutationContextProvider } from '../transport/components/UseDoLoginMutationContextProvider';
import { UseDoSignUpMutationContextProvider } from '../transport/components/UseDoSignUpMutationContextProvider';
import { IFaceLoader, NonAuthorizedLayout } from '@/bo-kit/components';
import { UseDoResetPasswordSendCodeMutationContextProvider } from '@/transport/components/UseDoResetPasswordSendCodeMutationContextProvider';
import { UseGetSliderItemsContextProvider } from '@/transport/components/UseGetSliderItemsContextProvider';
import { VerifyAccountPage } from '@/routes/components/VerifyAccountPage';
import { UseDoLogoutMutationContextProvider } from '@/transport/components/UseDoLogoutMutationContextProvider';
import { ResetPasswordCodePage } from '@/routes/components/ResetPasswordCodePage';
import { UseUserContextProvider } from '@/store/components/UseUserContextProvider';
import { AuthGuard } from '@/components/AuthGuard';

const ResetPasswordSuccessPageResponsive = lazy(
    () => import('@/bo-kit/components/ResetPasswordPage/components/Success')
);
const SignUpPageResponsive = lazy(
    () => import('@/bo-kit/components/SignUpPage/SignUpPageResponsive')
);
const NotFoundPage = lazy(() => import('@/bo-kit/components/NotFoundPage'));
const SignInPageResponsive = lazy(
    () => import('@/bo-kit/components/SignInPage/SignInPageResponsive')
);
const ResetPasswordNewPasswordPageResponsive = lazy(
    () => import('@/bo-kit/components/ResetPasswordPage/components/NewPassword')
);
const ResetPasswordPage = lazy(
    () => import('@/bo-kit/components/ResetPasswordPage/components/ResetPassword')
);
const LogoutPage = lazy(() => import('@/bo-kit/components/LogoutPage'));

const isInPWA = isInStandaloneMode();

const RoutesInner: FC = () => (
    <RoutesBase>
        <Route path='' element={<NonAuthorizedLayout />}>
            <Route
                index
                element={
                    <UseGetSliderItemsContextProvider>
                        <UseDoLoginMutationContextProvider>
                            <Suspense fallback={<IFaceLoader />}>
                                <SignInPageResponsive implementation='js' />
                            </Suspense>
                        </UseDoLoginMutationContextProvider>
                    </UseGetSliderItemsContextProvider>
                }
            />
            <Route
                path='signup'
                element={
                    <UseGetSliderItemsContextProvider>
                        <UseDoSignUpMutationContextProvider>
                            <Suspense fallback={<IFaceLoader />}>
                                <SignUpPageResponsive implementation='js' />
                            </Suspense>
                        </UseDoSignUpMutationContextProvider>
                    </UseGetSliderItemsContextProvider>
                }
            />
            <Route path='verify'>
                <Route index element={<VerifyAccountPage implementation='js' />} />
                <Route path=':id/:code' element={<VerifyAccountPage implementation='js' />} />
            </Route>
            <Route path='resetpassword'>
                <Route
                    index
                    element={
                        <UseDoResetPasswordSendCodeMutationContextProvider>
                            <Suspense fallback={<IFaceLoader />}>
                                <ResetPasswordPage implementation='js' />
                            </Suspense>
                        </UseDoResetPasswordSendCodeMutationContextProvider>
                    }
                />
                <Route
                    path='newpassword'
                    element={
                        <Suspense fallback={<IFaceLoader />}>
                            <ResetPasswordNewPasswordPageResponsive implementation='js' />
                        </Suspense>
                    }
                />
                <Route path='codeverify'>
                    <Route index element={<ResetPasswordCodePage />} />
                    <Route
                        path='success'
                        element={<ResetPasswordSuccessPageResponsive implementation='js' />}
                    />
                </Route>
            </Route>
            <Route
                path='logout'
                element={
                    <UseUserContextProvider>
                        <UseDoLogoutMutationContextProvider>
                            <Suspense fallback={<IFaceLoader />}>
                                <LogoutPage useExternalLogin={!isInPWA} />
                            </Suspense>
                        </UseDoLogoutMutationContextProvider>
                    </UseUserContextProvider>
                }
            />
        </Route>
        <Route
            path='*'
            element={
                <Suspense fallback={<IFaceLoader />}>
                    <NotFoundPage />
                </Suspense>
            }
        />
    </RoutesBase>
);

export const Routes: FC = () => (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
        <AuthGuard>
            <RoutesInner />
        </AuthGuard>
    </BrowserRouter>
);
